import React, { useState } from 'react';
import {
  CreditCard, Upload, History, CheckCircle, Clock, AlertCircle,
  DollarSign, Calendar, FileText, Download, Eye, Plus, X,
  ArrowRight, Receipt
} from 'lucide-react';

// Mock payment data
const balanceData = {
  outstanding: 19290,
  credit: 0,
  lastPayment: { amount: 8920, date: 'Dec 12, 2025', method: 'Bank Transfer' }
};

const paymentHistory = [
  { id: 'PAY-1245', date: 'Dec 12, 2025', amount: 8920, method: 'Bank Transfer', status: 'completed', invoice: 'INV-8498' },
  { id: 'PAY-1238', date: 'Dec 8, 2025', amount: 15680, method: 'Bank Transfer', status: 'completed', invoice: 'INV-8476' },
  { id: 'PAY-1221', date: 'Nov 25, 2025', amount: 12340, method: 'Card', status: 'completed', invoice: 'INV-8421' },
  { id: 'PAY-1198', date: 'Nov 18, 2025', amount: 9870, method: 'Bank Transfer', status: 'completed', invoice: 'INV-8398' },
];

const pendingInvoices = [
  { id: 'INV-8521', amount: 12450, dueDate: 'Dec 30, 2025', packages: 3 },
  { id: 'INV-8432', amount: 6840, dueDate: 'Dec 13, 2025', packages: 1, overdue: true },
];

export default function CustomerPayments() {
  const [activeTab, setActiveTab] = useState('pay');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSubmitPayment = () => {
    // Mock submission
    alert('Payment proof submitted! We will verify and update your account within 24 hours.');
    setShowUploadModal(false);
    setUploadedFile(null);
    setSelectedInvoice(null);
  };

  return (
    <div className="customer-payments-page" data-testid="customer-payments">
      {/* Balance Summary */}
      <div className="payment-balance-card">
        <div className="balance-main">
          <div className="balance-section">
            <span className="balance-label">Outstanding Balance</span>
            <span className="balance-amount outstanding">
              <DollarSign size={28} />
              JMD {balanceData.outstanding.toLocaleString()}
            </span>
          </div>
          {balanceData.credit > 0 && (
            <div className="balance-section credit">
              <span className="balance-label">Credit Balance</span>
              <span className="balance-amount credit">
                JMD {balanceData.credit.toLocaleString()}
              </span>
            </div>
          )}
        </div>
        <div className="last-payment-info">
          <span className="last-payment-label">Last Payment</span>
          <span className="last-payment-details">
            JMD {balanceData.lastPayment.amount.toLocaleString()} on {balanceData.lastPayment.date}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="payment-tabs">
        <button 
          className={`tab-btn ${activeTab === 'pay' ? 'active' : ''}`}
          onClick={() => setActiveTab('pay')}
          data-testid="tab-pay"
        >
          <CreditCard size={18} />
          Make Payment
        </button>
        <button 
          className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
          data-testid="tab-upload"
        >
          <Upload size={18} />
          Upload Proof
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          data-testid="tab-history"
        >
          <History size={18} />
          Payment History
        </button>
      </div>

      {/* Pay Tab */}
      {activeTab === 'pay' && (
        <div className="payment-pay-section" data-testid="pay-section">
          <h3>Pending Invoices</h3>
          <p className="section-description">Select an invoice to pay or pay your full balance</p>

          <div className="pending-invoices-list">
            {pendingInvoices.map((invoice) => (
              <div 
                key={invoice.id}
                className={`pending-invoice-card ${invoice.overdue ? 'overdue' : ''}`}
                data-testid={`pending-${invoice.id}`}
              >
                <div className="invoice-info">
                  <div className="invoice-header">
                    <Receipt size={18} />
                    <span className="invoice-id">{invoice.id}</span>
                    {invoice.overdue && (
                      <span className="overdue-badge">
                        <AlertCircle size={12} /> Overdue
                      </span>
                    )}
                  </div>
                  <div className="invoice-details">
                    <span>{invoice.packages} package(s)</span>
                    <span>Due: {invoice.dueDate}</span>
                  </div>
                </div>
                <div className="invoice-actions">
                  <span className="invoice-amount">JMD {invoice.amount.toLocaleString()}</span>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setShowUploadModal(true);
                    }}
                    data-testid={`pay-btn-${invoice.id}`}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pay Full Balance */}
          <div className="pay-full-balance-card">
            <div className="pay-full-info">
              <h4>Pay Full Balance</h4>
              <p>Clear all outstanding invoices in one payment</p>
            </div>
            <div className="pay-full-action">
              <span className="full-amount">JMD {balanceData.outstanding.toLocaleString()}</span>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setSelectedInvoice({ id: 'ALL', amount: balanceData.outstanding });
                  setShowUploadModal(true);
                }}
                data-testid="pay-full-balance"
              >
                Pay All <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Payment Methods Info */}
          <div className="payment-methods-info">
            <h4>Accepted Payment Methods</h4>
            <div className="methods-grid">
              <div className="method-card">
                <div className="method-icon bank">$</div>
                <div className="method-details">
                  <span className="method-name">Bank Transfer</span>
                  <span className="method-account">NCB: 123-456-789</span>
                </div>
              </div>
              <div className="method-card">
                <div className="method-icon card">CC</div>
                <div className="method-details">
                  <span className="method-name">Credit/Debit Card</span>
                  <span className="method-account">Coming Soon</span>
                </div>
              </div>
              <div className="method-card">
                <div className="method-icon mobile">M</div>
                <div className="method-details">
                  <span className="method-name">Mobile Money</span>
                  <span className="method-account">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Proof Tab */}
      {activeTab === 'upload' && (
        <div className="payment-upload-section" data-testid="upload-section">
          <h3>Upload Payment Proof</h3>
          <p className="section-description">
            Already made a bank transfer? Upload your receipt for faster processing.
          </p>

          <div className="upload-card">
            <div className="upload-zone" onClick={() => document.getElementById('proof-upload').click()}>
              <Upload size={48} />
              <h4>Drop your receipt here</h4>
              <p>or click to browse</p>
              <span className="upload-formats">Accepted: JPG, PNG, PDF (max 5MB)</span>
              <input
                type="file"
                id="proof-upload"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                data-testid="proof-file-input"
              />
            </div>

            {uploadedFile && (
              <div className="uploaded-file-preview">
                <FileText size={20} />
                <span>{uploadedFile.name}</span>
                <button 
                  className="remove-file"
                  onClick={() => setUploadedFile(null)}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            <div className="upload-form">
              <div className="form-group">
                <label>Select Invoice</label>
                <select data-testid="select-invoice">
                  <option value="">Select invoice...</option>
                  {pendingInvoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.id} - JMD {inv.amount.toLocaleString()}
                    </option>
                  ))}
                  <option value="ALL">Full Balance - JMD {balanceData.outstanding.toLocaleString()}</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Amount (JMD)</label>
                <input 
                  type="number" 
                  placeholder="Enter amount paid"
                  data-testid="payment-amount-input"
                />
              </div>

              <div className="form-group">
                <label>Reference Number (Optional)</label>
                <input 
                  type="text" 
                  placeholder="Bank reference or transaction ID"
                  data-testid="reference-input"
                />
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea 
                  placeholder="Any additional information..."
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              <button 
                className="btn btn-primary full-width"
                disabled={!uploadedFile}
                data-testid="submit-proof"
              >
                Submit Payment Proof
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="payment-history-section" data-testid="history-section">
          <div className="history-header">
            <h3>Payment History</h3>
            <button className="btn btn-secondary" data-testid="download-statement">
              <Download size={14} /> Download Statement
            </button>
          </div>

          <div className="payment-history-list">
            {paymentHistory.map((payment) => (
              <div key={payment.id} className="payment-history-card" data-testid={`payment-${payment.id}`}>
                <div className="payment-status-icon completed">
                  <CheckCircle size={20} />
                </div>
                <div className="payment-info">
                  <div className="payment-main">
                    <span className="payment-id">{payment.id}</span>
                    <span className="payment-date">{payment.date}</span>
                  </div>
                  <div className="payment-details">
                    <span className="payment-invoice">Invoice: {payment.invoice}</span>
                    <span className="payment-method">{payment.method}</span>
                  </div>
                </div>
                <div className="payment-amount">
                  <span>JMD {payment.amount.toLocaleString()}</span>
                  <span className={`status-badge ${payment.status}`}>{payment.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Payment Modal */}
      {showUploadModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div 
            className="modal-content payment-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="payment-modal"
          >
            <div className="modal-header">
              <h3>Make Payment</h3>
              <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="payment-summary">
                <span className="payment-for">
                  {selectedInvoice.id === 'ALL' ? 'Full Balance' : `Invoice ${selectedInvoice.id}`}
                </span>
                <span className="payment-total">JMD {selectedInvoice.amount.toLocaleString()}</span>
              </div>

              <div className="payment-instructions">
                <h4>Bank Transfer Details</h4>
                <div className="bank-details">
                  <div className="bank-row">
                    <span className="bank-label">Bank</span>
                    <span className="bank-value">National Commercial Bank</span>
                  </div>
                  <div className="bank-row">
                    <span className="bank-label">Account Name</span>
                    <span className="bank-value">Bolt Nexus Courier Ltd</span>
                  </div>
                  <div className="bank-row">
                    <span className="bank-label">Account Number</span>
                    <span className="bank-value">123-456-789-00</span>
                  </div>
                  <div className="bank-row">
                    <span className="bank-label">Reference</span>
                    <span className="bank-value">Your Name - {selectedInvoice.id}</span>
                  </div>
                </div>
              </div>

              <div className="upload-proof-section">
                <h4>Upload Payment Receipt</h4>
                <div 
                  className="mini-upload-zone"
                  onClick={() => document.getElementById('modal-proof-upload').click()}
                >
                  {uploadedFile ? (
                    <div className="uploaded-preview">
                      <FileText size={24} />
                      <span>{uploadedFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={24} />
                      <span>Click to upload receipt</span>
                    </>
                  )}
                  <input
                    type="file"
                    id="modal-proof-upload"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitPayment}
                disabled={!uploadedFile}
                data-testid="confirm-payment"
              >
                Submit Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
